// src/actprep-ui/hooks/auth/useAuth.js
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import Cookies from "js-cookie";
import { generateRandomString, generateCodeChallenge } from "../../utils/pkce";
import { urls } from "../../utils/urls";

// Create the AuthContext
const AuthContext = createContext({ token: null, refreshToken: () => {} });

export const AuthProvider = ({ children }) => {
  // Retrieve the access token from cookies (if present)
  const [token, setToken] = useState(Cookies.get("access_token") || null);
  // We'll store the computed expiration time (in ms) based on expires_in.
  const [expiresAt, setExpiresAt] = useState(null);

  // Login function: If no token exists, redirect the user to the OAuth provider.
  const login = useCallback(() => {
    // Save current URL for redirect after login.
    localStorage.setItem("redirectAfterLogin", window.location.pathname);

    // Generate PKCE code verifier and store it.
    const codeVerifier = generateRandomString(64);
    localStorage.setItem("pkce_code_verifier", codeVerifier);

    // Generate the code challenge and redirect once computed.
    generateCodeChallenge(codeVerifier).then((codeChallenge) => {
      const clientId = process.env.REACT_APP_CLIENT_ID;
      const redirectUri = encodeURIComponent(urls.sso.redirect());
      const scope = encodeURIComponent("openid"); // Using OpenID scope

      const authUrl = `${urls.sso.authorization()}?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&scope=${scope}&code_challenge=${codeChallenge}&code_challenge_method=S256`;
      window.location.href = authUrl;
    });
  }, []);

  // If no token exists, trigger the login flow.
  useEffect(() => {
    if (!token && !window.location.href.includes(urls.sso.redirect())) {
      login();
    }
  }, [token, login]);

  // Refresh token function using the expires_in value.
  const refreshToken = useCallback(async () => {
    const refresh_token = Cookies.get("refresh_token");
    if (!refresh_token) {
      console.error("No refresh token available for refresh.");
      return;
    }
    const clientId = process.env.REACT_APP_CLIENT_ID;
    const tokenUrl = process.env.REACT_APP_TOKEN_URL || urls.sso.token();

    const data = new URLSearchParams();
    data.append("grant_type", "refresh_token");
    data.append("refresh_token", refresh_token);
    data.append("client_id", clientId);

    try {
      const response = await fetch(tokenUrl, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });
      const tokenData = await response.json();
      if (response.status == 400 || !response.ok) {
        Cookies.remove("access_token");
        Cookies.remove("refresh_token");
        window.location.reload();
        return;
      }
      if (tokenData.access_token) {
        // Update cookies with new token values.
        Cookies.set("access_token", tokenData.access_token, { expires: 1 });
        if (tokenData.refresh_token) {
          Cookies.set("refresh_token", tokenData.refresh_token, { expires: 7 });
        }
        setToken(tokenData.access_token);
        // Compute expiration time using the expires_in value.
        if (tokenData.expires_in) {
          setExpiresAt(Date.now() + tokenData.expires_in * 1000);
        }
      } else {
        console.error("Token refresh error:", tokenData);
      }
    } catch (error) {
      console.error("Token refresh failed:", error);
    }
  }, []);

  // Set up a timer to refresh the token before it expires.
  useEffect(() => {
    if (!token) return;

    // Use expiresAt if available; otherwise, assume default expiration (24 hours).
    const defaultExpireSeconds = 3600 * 24;
    const expirationTime =
      expiresAt || Date.now() + defaultExpireSeconds * 1000;
    const now = Date.now();
    // Schedule the refresh 1 minute before expiration.
    const timeout = expirationTime - now - 60000;

    if (timeout <= 0) {
      refreshToken();
    } else {
      const timer = setTimeout(() => {
        refreshToken();
      }, timeout);
      return () => clearTimeout(timer);
    }
  }, [token, expiresAt, refreshToken]);

  return (
    <AuthContext.Provider value={{ token, refreshToken, setToken }}>
      {children}
    </AuthContext.Provider>
  );
};

// Export the useAuth hook as the default export.
export default function useAuth() {
  return useContext(AuthContext);
}
